import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFormContext } from "react-hook-form";
import { forwardRef } from "react";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons";

import { T, useT } from "@repo/transifex";
import { employeeActions } from "~/providers/store/employee";

import type { InvoiceStepSelectorsType } from "../route";

type StepSelectorsProps = {
  selectors: string[];
  userGroupName: string | null;
  isUserAuthenticated: boolean;
};

export const StepSelectors = ({
  selectors,
  userGroupName,
  isUserAuthenticated
}: StepSelectorsProps) => {
  const t = useT();

  const {
    register,
    formState: { errors },
    getValues
  } = useFormContext<InvoiceStepSelectorsType>();

  const selectorValue = getValues("selector") ?? null;

  if (userGroupName && isUserAuthenticated) {
    return (
      <div className="flex w-full flex-col items-center gap-3">
        <div className="grid w-full grid-cols-4 gap-2">
          <SelectorRadioButton
            value={userGroupName}
            {...register("selector", {
              required: t("Group selection is required")
            })}
            defaultChecked={selectorValue === userGroupName}
          />
        </div>
      </div>
    );
  }

  if (!selectors.length) return <T _str="No results found" />;

  return (
    <div className="flex w-full flex-col items-center gap-3">
      <div className="grid w-full grid-cols-4 gap-2">
        {selectors.map((selector) => (
          <SelectorRadioButton
            key={selector}
            value={selector}
            {...register("selector", {
              required: t("Group selection is required")
            })}
            onClick={() => {
              employeeActions.setAuthEmployee({ groupName: selector });
            }}
          />
        ))}
      </div>
      {errors.selector ? (
        <span className="text-danger-main">{errors.selector.message}</span>
      ) : null}
    </div>
  );
};

type SelectorRadioProps = {
  value: string;
} & JSX.IntrinsicElements["input"];

const SelectorRadioButton = forwardRef<HTMLInputElement, SelectorRadioProps>(
  ({ value, ...props }, ref) => {
    return (
      <label className="h-20 min-w-[20%] max-w-full grow cursor-pointer font-medium">
        <input
          className="peer hidden"
          ref={ref}
          type="radio"
          value={value}
          {...props}
        />
        <span className="flex size-full items-center justify-center gap-2 rounded-md border border-info-backdrop bg-background-primary px-4 peer-checked:bg-info-main peer-checked:text-primary-contrast peer-checked:*:block">
          <FontAwesomeIcon className="hidden" icon={faCheckCircle} />
          {value}
        </span>
      </label>
    );
  }
);

SelectorRadioButton.displayName = "SelectorRadioButton";
